import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Theme } from '@carbon/react';
import { Close, Task as TaskIcon } from '@carbon/icons-react';
import axiosInstance from '../../utils/axiosInstance';
import { useTheme } from '../../contexts/ThemeContext';
import {
  formatDate,
  formatDateTime,
  formatTime,
  getResidentName,
  getRoomName,
  getTaskTitle,
} from './workDayUtils';

// Naziv statusa zadatka koji backend vraća (waiting / in_progress / completed / canceled)
const STATUS_LABELS = {
  waiting: 'Čeka izvršenje',
  in_progress: 'U toku',
  inprogress: 'U toku',
  completed: 'Završen',
  canceled: 'Otkazan',
  cancelled: 'Otkazan',
};

const statusKey = (status) => String(status || '').toLowerCase();

const statusClass = (status) => {
  const key = statusKey(status);
  if (key === 'completed') return 'done';
  if (key === 'canceled' || key === 'cancelled') return 'canceled';
  if (key === 'in_progress' || key === 'inprogress') return 'progress';
  return 'waiting';
};

const statusLabel = (status) => STATUS_LABELS[statusKey(status)] || status || '-';

// Prioritet zadatka (1 - veoma važno, 2 - važno, 3 - normalno)
const severityLabel = (severity) => {
  if (severity === 1) return 'Veoma važno';
  if (severity === 2) return 'Važno';
  if (severity === 3) return 'Normalno';
  return 'Važno';
};

const severityClass = (severity) => {
  if (severity === 1) return 'high';
  if (severity === 3) return 'low';
  return 'medium';
};

// Nazivi tipova zaposlenih koji prate zadatak (`employeeTypeNames` je poravnat po
// indeksu sa `employeeTypeIds`). Fallback na legacy jedninu.
const getTaskEmployeeTypeNames = (task) => {
  if (!task) return '';
  if (Array.isArray(task.employeeTypeNames) && task.employeeTypeNames.length > 0) {
    return task.employeeTypeNames.join(', ');
  }
  return task.employeeTypeName || '';
};

const InfoRow = ({ label, value }) => (
  <div className="wdash-detail__row">
    <span className="wdash-detail__label">{label}</span>
    <span className="wdash-detail__value">{value}</span>
  </div>
);

/**
 * TaskDetailsDrawer - desni panel sa detaljima izabranog zadatka.
 *
 * Panel je uvek montiran (radi slide animacije), a `.open` ga uvodi u vidno polje.
 * Odmah prikazuje podatke iz reda liste (da nema praznog ekrana), a pune detalje
 * dopunjava sa GET /api/Task/{id} (isti poziv kao na stranici Zadaci).
 */
const TaskDetailsDrawer = ({ task, onClose }) => {
  const { theme } = useTheme();
  const isOpen = Boolean(task);
  const taskId = task?.id ?? null;
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (taskId == null) {
      setDetails(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setDetails(null);
    setLoading(true);

    axiosInstance
      .get(`/api/Task/${taskId}`)
      .then((response) => {
        if (cancelled) return;
        const body = response.data;
        const payload = body && body.data ? body.data : body;
        setDetails(payload || null);
      })
      .catch((err) => {
        if (cancelled) return;
        // Zadrži podatke iz reda liste ako detalji ne mogu da se učitaju
        console.error('Greška pri učitavanju detalja zadatka:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [taskId]);

  // Escape zatvara panel (kao i klik na overlay ili dugme za zatvaranje)
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const data = details || task;
  const resident = data ? getResidentName(data) : '';
  const room = data ? getRoomName(data) : '';
  const typeName = data ? data.mealTypeName || data.activityTypeName || data.activityType || '' : '';
  const employeeTypeNames = data ? getTaskEmployeeTypeNames(data) : '';
  const assignee = data?.assignToAll ? 'Svi zaposleni' : data?.assignedToName || 'Nije dodeljen';

  return ReactDOM.createPortal(
    <>
      <Theme theme={theme}>
        <div
          className={`wdash-drawer ${isOpen ? 'open' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="wdash-task-details-title"
          aria-hidden={!isOpen}
        >
          <div className="wdash-drawer__head">
            <h3 className="wdash-drawer__title" id="wdash-task-details-title">
              Detalji zadatka
            </h3>
            <button
              type="button"
              className="wdash-drawer__icon-btn"
              onClick={onClose}
              aria-label="Zatvori detalje zadatka"
            >
              <Close size={18} />
            </button>
          </div>

          <div className="wdash-drawer__body">
            {loading && !data ? (
              <div className="wdash-task-detail__skeleton">
                <span />
                <span />
                <span />
                <span />
              </div>
            ) : data ? (
              <div className="wdash-task-detail">
                <div className="wdash-task-detail__hero">
                  <span className="wdash-task-detail__icon">
                    <TaskIcon size={20} />
                  </span>
                  <h4 className="wdash-task-detail__title">{getTaskTitle(data)}</h4>
                </div>

                <div className="wdash-task-detail__chips">
                  <span className={`wdash-chip wdash-chip--${statusClass(data.status)}`}>
                    {statusLabel(data.status)}
                  </span>
                  {data.severity != null && (
                    <span className={`wdash-chip wdash-chip--${severityClass(data.severity)}`}>
                      {severityLabel(data.severity)}
                    </span>
                  )}
                  {data.assignToAll && <span className="wdash-chip">Dodeljeno svima</span>}
                </div>

                {data.description && (
                  <div className="wdash-task-detail__section">
                    <span className="wdash-task-detail__label">Opis</span>
                    <p className="wdash-task-detail__value">{data.description}</p>
                  </div>
                )}

                {data.cancelNote && (
                  <div className="wdash-task-detail__section">
                    <span className="wdash-task-detail__label">Napomena o otkazivanju</span>
                    <p className="wdash-task-detail__value wdash-task-detail__value--danger">
                      {data.cancelNote}
                    </p>
                  </div>
                )}

                <div className="wdash-task-detail__section">
                  <span className="wdash-task-detail__label">Informacije</span>
                  <div className="wdash-detail__info">
                    <InfoRow label="Korisnik" value={resident || '-'} />
                    {room && <InfoRow label="Soba" value={room} />}
                    <InfoRow label="Tip zadatka" value={typeName || '-'} />
                    <InfoRow
                      label="Zakazano vreme"
                      value={
                        data.scheduledTime
                          ? `${formatDate(data.scheduledTime)} ${formatTime(data.scheduledTime)}`
                          : '-'
                      }
                    />
                    <InfoRow label="Zadužena osoba" value={assignee} />
                    {employeeTypeNames && <InfoRow label="Prati" value={employeeTypeNames} />}
                    {data.createdByName && <InfoRow label="Kreirao" value={data.createdByName} />}
                    {data.executedAt && (
                      <InfoRow label="Izvršeno" value={formatDateTime(data.executedAt)} />
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="wdash-drawer__empty">Nema detalja zadatka</p>
            )}
          </div>
        </div>
      </Theme>

      {isOpen && <div className="wdash-drawer-overlay" onClick={onClose} />}
    </>,
    document.body
  );
};

export default TaskDetailsDrawer;
